import { useCallback, useEffect, useState } from "react";
import api from "../api";
import { useAuth } from "../AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "./FormField";
import { eur, fmtDate } from "../helpers";
import {
  Users, TriangleAlert, Plus, Trash2, Link2, Copy, MessageCircle, Mail,
  RefreshCw, CheckCircle2, RotateCcw, Ban, CreditCard, Banknote, Loader2,
} from "lucide-react";

const EXTRA_TYPES = {
  ospite_aggiuntivo: "Ospite aggiuntivo",
  pulizia_extra: "Pulizia extra",
  letto_extra: "Letto extra",
  kit_cortesia: "Kit cortesia",
  late_checkout: "Late check-out",
  early_checkin: "Early check-in",
  parcheggio: "Parcheggio",
  cauzione: "Cauzione",
  custom: "Personalizzato",
};

const STATUS_LABEL = {
  created: "Creato", pending: "In attesa", paid: "Pagato", failed: "Fallito",
  expired: "Scaduto", refunded: "Rimborsato", partially_refunded: "Rimborso parziale", cancelled: "Annullato",
};
const STATUS_STYLE = {
  paid: "bg-emerald-100 text-emerald-700", pending: "bg-amber-100 text-amber-800",
  created: "bg-slate-100 text-slate-600", failed: "bg-rose-100 text-rose-700",
  expired: "bg-slate-200 text-slate-600", refunded: "bg-rose-100 text-rose-700",
  partially_refunded: "bg-orange-100 text-orange-700", cancelled: "bg-slate-200 text-slate-500",
};
const PURPOSES = [
  { key: "saldo", label: "Saldo prenotazione" },
  { key: "acconto", label: "Acconto" },
  { key: "cauzione", label: "Cauzione" },
  { key: "tassa_soggiorno", label: "Tassa di soggiorno" },
  { key: "custom", label: "Personalizzato" },
];
const ADMIN = ["super_admin", "admin", "amministratore"];

const PBadge = ({ s }) => (
  <span data-testid={`pay-status-${s}`} className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_STYLE[s] || "bg-slate-100 text-slate-600"}`}>
    {STATUS_LABEL[s] || s}
  </span>
);

export default function PaymentDetailDialog({ bookingId, open, onOpenChange, onChanged }) {
  const { user } = useAuth();
  const isAdmin = ADMIN.includes(user?.role);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [extra, setExtra] = useState({ type: "ospite_aggiuntivo", qty: 1, amount: "", label: "", notes: "" });
  const [pay, setPay] = useState({ provider: "stripe", purpose: "saldo", amount: "" });

  const load = useCallback(async () => {
    if (!bookingId) return;
    try {
      const r = await api.get(`/bookings/${bookingId}/payment-overview`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore caricamento"); }
  }, [bookingId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const refresh = async () => { await load(); onChanged?.(); };

  // ---- extras ----
  const addExtra = async () => {
    setBusy(true);
    try {
      await api.post(`/bookings/${bookingId}/extras`, {
        type: extra.type, qty: Number(extra.qty) || 1,
        amount: extra.amount === "" ? 0 : Number(extra.amount),
        label: extra.label, notes: extra.notes,
      });
      toast.success("Extra aggiunto");
      setExtra({ type: "ospite_aggiuntivo", qty: 1, amount: "", label: "", notes: "" });
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setBusy(false); }
  };
  const delExtra = async (eid) => {
    try { await api.delete(`/bookings/${bookingId}/extras/${eid}`); toast.success("Extra annullato"); await refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  // ---- payments ----
  const createPayment = async (purpose, provider, amount, label, extraId) => {
    setBusy(true);
    try {
      const body = { booking_id: bookingId, provider, purpose, origin_url: window.location.origin };
      if (amount != null) body.amount = Number(amount);
      if (label) body.label = label;
      if (extraId) body.extra_id = extraId;
      const r = await api.post(`/payments`, body);
      if (r.data.payment_link) {
        await navigator.clipboard?.writeText(r.data.payment_link).catch(() => {});
        toast.success("Link di pagamento generato e copiato");
      } else {
        toast.success("Pagamento registrato");
      }
      setPay({ provider: "stripe", purpose: "saldo", amount: "" });
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setBusy(false); }
  };
  const payExtraLink = (ex) => createPayment("extra", pay.provider === "sumup" ? "sumup" : "stripe", null, ex.label, ex.id);

  const copyLink = async (link) => { await navigator.clipboard?.writeText(link); toast.success("Link copiato"); };
  const sendWA = async (p) => {
    const phone = (data.booking.guest_whatsapp || "").replace(/[^0-9]/g, "");
    const msg = encodeURIComponent(`Gentile ${data.booking.guest_name}, ecco il link per il pagamento di ${p.label} (${eur(p.amount)}): ${p.payment_link}`);
    window.open(`https://wa.me/${phone}?text=${msg}`, "_blank");
    logComm(p, "whatsapp");
  };
  const sendEmail = (p) => {
    const subj = encodeURIComponent(`Richiesta pagamento · ${p.label}`);
    const body = encodeURIComponent(`Gentile ${data.booking.guest_name},\n\nÈ possibile completare il pagamento di ${p.label} (${eur(p.amount)}) al seguente link:\n${p.payment_link}\n\nGrazie,\nUrban Dreams Home`);
    window.open(`mailto:${data.booking.guest_email}?subject=${subj}&body=${body}`, "_blank");
    logComm(p, "email");
  };
  const logComm = (p, channel) => {
    api.post(`/bookings/${bookingId}/communications`, {
      channel, template_type: "custom", recipient: channel === "email" ? data.booking.guest_email : data.booking.guest_whatsapp,
      subject: `Richiesta pagamento · ${p.label}`, body: `Link pagamento ${p.label} ${eur(p.amount)}: ${p.payment_link}`,
    }).catch(() => {});
  };
  const verify = async (p) => {
    try { const r = await api.get(`/payments/${p.id}/status`); toast.message(`Stato: ${STATUS_LABEL[r.data.status] || r.data.status}`); await refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const markPaid = async (p) => {
    try { await api.post(`/payments/${p.id}/mark-paid`, {}); toast.success("Pagamento incassato"); await refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const cancelPay = async (p) => {
    try { await api.post(`/payments/${p.id}/cancel`); toast.success("Pagamento annullato"); await refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const refundPay = async (p) => {
    const raw = window.prompt(`Importo da rimborsare (max ${eur(p.amount - (p.refunded_amount || 0))}):`, String(p.amount - (p.refunded_amount || 0)));
    if (raw === null) return;
    const reason = window.prompt("Motivo del rimborso:") || "";
    try { await api.post(`/payments/${p.id}/refund`, { amount: Number(raw), reason }); toast.success("Rimborso registrato"); await refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const f = data?.financial;
  const gc = data?.guest_check;
  const providers = data?.providers || [];
  const onlineProviders = providers.filter((p) => p.online);
  const manualProviders = providers.filter((p) => !p.online);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl" data-testid="payment-detail-dialog">
        <DialogHeader>
          <DialogTitle>Gestione pagamenti · {data?.booking?.guest_name || "…"}</DialogTitle>
          <DialogDescription>
            {data?.booking && `${fmtDate(data.booking.check_in)} → ${fmtDate(data.booking.check_out)} · ${data.booking.channel}`}
          </DialogDescription>
        </DialogHeader>

        {!data ? (
          <div className="flex items-center justify-center py-12 text-slate-400"><Loader2 className="animate-spin" /></div>
        ) : (
          <div className="space-y-5">
            {/* Financial summary */}
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="fin-summary">
              {[
                { l: "Totale", v: f.total, c: "text-slate-900" },
                { l: "Incassato", v: f.paid, c: "text-emerald-600" },
                { l: "Saldo", v: f.balance, c: f.balance > 0 ? "text-rose-600" : "text-emerald-600" },
                { l: "Cauzione", v: f.deposit, c: "text-indigo-600" },
              ].map((k) => (
                <div key={k.l} className="rounded-lg border border-slate-200 p-3">
                  <div className={`text-lg font-semibold ${k.c}`}>{eur(k.v)}</div>
                  <div className="text-[11px] uppercase text-slate-400">{k.l}</div>
                </div>
              ))}
            </div>
            <div className="text-xs text-slate-500">
              Pernottamento {eur(f.nightly)} · Pulizia {eur(f.cleaning)} · Extra {eur(f.extra)} · Tassa soggiorno {eur(f.city_tax)}
            </div>

            {/* Guest control */}
            <div className={`rounded-lg border p-3 ${gc.warning ? "border-rose-200 bg-rose-50/50" : "border-emerald-200 bg-emerald-50/40"}`} data-testid="guest-control">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
                {gc.warning ? <TriangleAlert size={16} className="text-rose-600" /> : <Users size={16} className="text-emerald-600" />}
                Controllo ospiti
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
                <div><span className="text-slate-400">Dichiarati:</span> <b>{gc.declared}</b></div>
                <div><span className="text-slate-400">Registrati:</span> <b className={gc.registered < gc.declared ? "text-rose-600" : ""}>{gc.registered}</b></div>
                <div><span className="text-slate-400">Documenti:</span> <b className={gc.documents < gc.declared ? "text-rose-600" : ""}>{gc.documents}</b></div>
                <div><span className="text-slate-400">Adulti/Minori:</span> <b>{gc.adults}/{gc.minors}</b></div>
              </div>
              {gc.warning && (
                <div className="mt-2 text-xs text-rose-700" data-testid="guest-warning">
                  ⚠ Documenti/registrazioni inferiori agli ospiti dichiarati.
                  {gc.tally_link && <> <a href={gc.tally_link} target="_blank" rel="noreferrer" className="ml-1 font-semibold underline">Invia link Tally →</a></>}
                </div>
              )}
            </div>

            {/* Extras */}
            <div>
              <div className="mb-2 text-xs font-semibold uppercase text-slate-500">Extra soggiorno</div>
              <div className="space-y-2">
                {data.extras.length === 0 && <div className="text-sm text-slate-400">Nessun extra.</div>}
                {data.extras.map((ex) => (
                  <div key={ex.id} data-testid={`extra-${ex.id}`} className="flex items-center justify-between rounded-lg border border-slate-200 p-2.5 text-sm">
                    <div>
                      <div className="font-medium text-slate-800">{ex.label} <span className="text-slate-400">×{ex.qty}</span></div>
                      <div className="text-xs text-slate-400">{EXTRA_TYPES[ex.type] || ex.type} · {eur(ex.amount)} · {ex.status === "pagato" ? <span className="text-emerald-600">pagato</span> : "non pagato"}</div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {ex.status !== "pagato" && (
                        <>
                          <Button size="sm" variant="outline" data-testid={`extra-paylink-${ex.id}`} onClick={() => payExtraLink(ex)} disabled={busy}>
                            <Link2 size={13} className="mr-1" />Link
                          </Button>
                          <Button size="sm" variant="ghost" className="text-rose-600" data-testid={`extra-del-${ex.id}`} onClick={() => delExtra(ex.id)}>
                            <Trash2 size={14} />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {/* Add extra */}
              <div className="mt-3 rounded-lg bg-secondary/40 p-3" data-testid="add-extra-form">
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <Field label="Tipo">
                    <Select value={extra.type} onValueChange={(v) => setExtra((s) => ({ ...s, type: v }))}>
                      <SelectTrigger data-testid="extra-type"><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.entries(EXTRA_TYPES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Q.tà"><Input type="number" data-testid="extra-qty" value={extra.qty} onChange={(e) => setExtra((s) => ({ ...s, qty: e.target.value }))} /></Field>
                  <Field label="Importo € (auto)"><Input type="number" data-testid="extra-amount" placeholder={extra.type === "ospite_aggiuntivo" ? `${data.extra_guest_price}/ospite` : ""} value={extra.amount} onChange={(e) => setExtra((s) => ({ ...s, amount: e.target.value }))} /></Field>
                  <Field label="Etichetta"><Input data-testid="extra-label" value={extra.label} onChange={(e) => setExtra((s) => ({ ...s, label: e.target.value }))} /></Field>
                </div>
                <Button size="sm" className="mt-2 bg-primary hover:bg-primary/90" data-testid="add-extra-btn" disabled={busy} onClick={addExtra}>
                  <Plus size={14} className="mr-1" />Aggiungi extra
                </Button>
              </div>
            </div>

            {/* Payments list */}
            <div>
              <div className="mb-2 text-xs font-semibold uppercase text-slate-500">Transazioni</div>
              <div className="space-y-2">
                {data.payments.length === 0 && <div className="text-sm text-slate-400">Nessuna transazione.</div>}
                {data.payments.map((p) => (
                  <div key={p.id} data-testid={`payment-${p.id}`} className="rounded-lg border border-slate-200 p-3 text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <div className="font-medium text-slate-800">{eur(p.amount)} · {p.label} <PBadge s={p.status} /></div>
                        <div className="text-xs text-slate-400">{p.provider} · {fmtDate(p.created_at)}{Number(p.refunded_amount) > 0 ? ` · rimb. ${eur(p.refunded_amount)}` : ""}</div>
                      </div>
                      <div className="flex flex-wrap items-center justify-end gap-1.5">
                        {p.payment_link && ["pending", "created"].includes(p.status) && (
                          <>
                            <Button size="sm" variant="outline" data-testid={`copy-${p.id}`} onClick={() => copyLink(p.payment_link)}><Copy size={13} /></Button>
                            <Button size="sm" variant="outline" className="text-emerald-600" data-testid={`wa-${p.id}`} onClick={() => sendWA(p)}><MessageCircle size={13} /></Button>
                            <Button size="sm" variant="outline" className="text-blue-600" data-testid={`email-${p.id}`} onClick={() => sendEmail(p)}><Mail size={13} /></Button>
                            <Button size="sm" variant="outline" data-testid={`verify-${p.id}`} onClick={() => verify(p)}><RefreshCw size={13} /></Button>
                          </>
                        )}
                        {["pending", "created"].includes(p.status) && (
                          <>
                            <Button size="sm" variant="outline" className="text-emerald-600" data-testid={`markpaid-${p.id}`} onClick={() => markPaid(p)}><CheckCircle2 size={13} className="mr-1" />Incassa</Button>
                            <Button size="sm" variant="ghost" className="text-slate-500" data-testid={`cancel-${p.id}`} onClick={() => cancelPay(p)}><Ban size={13} /></Button>
                          </>
                        )}
                        {isAdmin && ["paid", "partially_refunded"].includes(p.status) && (p.amount - (p.refunded_amount || 0) > 0.01) && (
                          <Button size="sm" variant="outline" className="text-rose-600 border-rose-200" data-testid={`refund-${p.id}`} onClick={() => refundPay(p)}><RotateCcw size={13} className="mr-1" />Rimborsa</Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Create payment */}
            <div className="rounded-lg border border-slate-200 p-3" data-testid="create-payment-form">
              <div className="mb-2 text-xs font-semibold uppercase text-slate-500">Nuovo pagamento</div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                <Field label="Causale">
                  <Select value={pay.purpose} onValueChange={(v) => setPay((s) => ({ ...s, purpose: v }))}>
                    <SelectTrigger data-testid="pay-purpose"><SelectValue /></SelectTrigger>
                    <SelectContent>{PURPOSES.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Metodo">
                  <Select value={pay.provider} onValueChange={(v) => setPay((s) => ({ ...s, provider: v }))}>
                    <SelectTrigger data-testid="pay-provider"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {onlineProviders.map((p) => <SelectItem key={p.key} value={p.key} disabled={!p.configured}>{p.label}{!p.configured ? " (non config.)" : " · link"}</SelectItem>)}
                      {manualProviders.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                {pay.purpose !== "saldo" && (
                  <Field label="Importo €"><Input type="number" data-testid="pay-amount" value={pay.amount} onChange={(e) => setPay((s) => ({ ...s, amount: e.target.value }))} /></Field>
                )}
              </div>
              <Button size="sm" className="mt-2 bg-indigo-600 hover:bg-indigo-700" data-testid="create-payment-btn" disabled={busy}
                onClick={() => createPayment(pay.purpose, pay.provider, pay.purpose === "saldo" ? null : pay.amount)}>
                {onlineProviders.some((p) => p.key === pay.provider) ? <><CreditCard size={14} className="mr-1" />Genera link</> : <><Banknote size={14} className="mr-1" />Registra pagamento</>}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
